import { useMemo, useState } from 'react';
import { useAuthContext } from '../../context/AuthContext';
import { useProfileContext } from '../../context/ProfileContext';
import { useChapterQuestions } from '../../hooks/useChapterQuestions';
import AskQuestionModal from './AskQuestionModal';
import ConfirmDeleteModal from './ConfirmDeleteModal';
import ReportQuestionModal from './ReportQuestionModal';

function CommentAvatar({ avatar, initials, author }) {
  if (avatar) {
    return <img src={avatar} alt={author} className="h-10 w-10 rounded-full object-cover" />;
  }

  return (
    <div className="flex h-10 w-10 items-center justify-center rounded-full bg-[#9369CC] text-sm font-bold text-white">
      {initials || author?.charAt(0) || '?'}
    </div>
  );
}

function CommentActions({
  comment,
  onLike,
  onDislike,
  onReply,
  showReply = true,
}) {
  return (
    <div className="mt-4 flex flex-wrap items-center gap-4 text-sm text-[#666666]">
      <button
        type="button"
        onClick={() => onLike(comment)}
        className={`flex items-center gap-1.5 transition hover:text-[#1A87CC] ${
          comment.userReaction === 'like' ? 'text-[#1A87CC]' : ''
        }`}
      >
        <svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.8}>
          <path strokeLinecap="round" strokeLinejoin="round" d="M14 10h4.764a2 2 0 011.789 2.894l-3.5 7A2 2 0 0115.263 21h-4.017c-.163 0-.326-.02-.485-.06L7 20m7-10V5a2 2 0 00-2-2h-.095c-.5 0-.905.405-.905.905 0 .714-.211 1.412-.608 2.006L7 11v9m7-10h-2M7 20H5a2 2 0 01-2-2v-6a2 2 0 012-2h2.5" />
        </svg>
        {comment.likes}
      </button>
      <button
        type="button"
        onClick={() => onDislike(comment)}
        className={`flex items-center gap-1.5 transition hover:text-[#1A87CC] ${
          comment.userReaction === 'dislike' ? 'text-[#1A87CC]' : ''
        }`}
      >
        <svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.8}>
          <path strokeLinecap="round" strokeLinejoin="round" d="M10 14H5.236a2 2 0 01-1.789-2.894l3.5-7A2 2 0 018.736 3h4.018a2 2 0 01.485.06l3.76.94m-7 10v5a2 2 0 002 2h.096c.5 0 .905-.405.905-.904 0-.715.211-1.413.608-2.008L17 13V4m-7 10h2m5-10h2a2 2 0 012 2v6a2 2 0 01-2 2h-2.5" />
        </svg>
        {comment.dislikes}
      </button>
      {showReply && (
        <button
          type="button"
          onClick={() => onReply(comment)}
          className="flex items-center gap-1.5 transition hover:text-[#1A87CC]"
        >
          <svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.8}>
            <path strokeLinecap="round" strokeLinejoin="round" d="M8 12h.01M12 12h.01M16 12h.01M21 12c0 4.418-4.03 8-9 8a9.863 9.863 0 01-4.255-.949L3 20l1.395-3.72C3.512 15.042 3 13.574 3 12c0-4.418 4.03-8 9-8s9 3.582 9 8z" />
          </svg>
          {comment.replies} Reply
        </button>
      )}
      <span className="ml-auto text-xs text-[#999999]">{comment.timeAgo}</span>
    </div>
  );
}

function CommentMenu({ comment, open, onToggle, onClose, onPin, onEdit, onReport, onDelete }) {
  const items = [
    comment.canPin
      ? { key: 'pin', label: comment.isPinned ? 'Unpin' : 'Pin', onClick: () => onPin(comment) }
      : null,
    comment.canEdit ? { key: 'edit', label: 'Edit', onClick: () => onEdit(comment) } : null,
    comment.type === 'question'
      ? { key: 'report', label: 'Report', onClick: () => onReport(comment) }
      : null,
    comment.canDelete ? { key: 'delete', label: 'Delete', onClick: () => onDelete(comment) } : null,
  ].filter(Boolean);

  if (!items.length) return null;

  return (
    <div className="relative shrink-0">
      <button
        type="button"
        onClick={onToggle}
        className="text-[#999999]"
        aria-label="More options"
      >
        <svg className="h-5 w-5" fill="currentColor" viewBox="0 0 20 20">
          <path d="M10 6a2 2 0 110-4 2 2 0 010 4zM10 12a2 2 0 110-4 2 2 0 010 4zM10 18a2 2 0 110-4 2 2 0 010 4z" />
        </svg>
      </button>
      {open && (
        <div className="absolute right-0 top-8 z-10 min-w-[120px] rounded-lg border border-[#E8E8E8] bg-white py-1 shadow-lg">
          {items.map((item) => (
            <button
              key={item.key}
              type="button"
              onClick={() => {
                item.onClick();
                onClose();
              }}
              className="flex w-full px-3 py-2 text-left text-sm text-[#666666] hover:bg-[#F5F5F5]"
            >
              {item.label}
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

function DiscussionComment({
  comment,
  depth = 0,
  menuOpenId,
  onToggleMenu,
  onCloseMenu,
  onLike,
  onDislike,
  onReply,
  onPin,
  onEdit,
  onReport,
  onDelete,
}) {
  return (
    <div className={depth > 0 ? 'relative ml-6 border-l-2 border-[#C5E4FF] pl-6' : ''}>
      <article className="relative rounded-2xl bg-white p-5 shadow-[0_2px_14px_rgba(0,0,0,0.05)]">
        <div className="flex items-start gap-3">
          <CommentAvatar avatar={comment.avatar} initials={comment.initials} author={comment.author} />
          <div className="min-w-0 flex-1">
            <div className="flex flex-wrap items-center gap-2">
              <h3 className="text-sm font-semibold text-[#1B1B1B]">{comment.author}</h3>
              {comment.isExpert && (
                <span className="flex items-center gap-1 text-xs font-medium text-[#1A87CC]">
                  Expert
                </span>
              )}
              {comment.isPinned && (
                <span className="rounded-full bg-[#9369CC] px-2.5 py-0.5 text-[10px] font-semibold uppercase text-white">
                  Pinned
                </span>
              )}
              {comment.isCorrectAnswer && (
                <span className="rounded-full bg-[#69991D] px-2.5 py-0.5 text-[10px] font-semibold uppercase text-white">
                  Correct Answer
                </span>
              )}
            </div>
            <p className="mt-3 text-sm leading-relaxed text-[#666666]">{comment.text}</p>
            <CommentActions
              comment={comment}
              onLike={onLike}
              onDislike={onDislike}
              onReply={onReply}
              showReply={comment.type === 'question'}
            />
          </div>
          <CommentMenu
            comment={comment}
            open={menuOpenId === `${comment.type}-${comment.id}`}
            onToggle={() => onToggleMenu(`${comment.type}-${comment.id}`)}
            onClose={onCloseMenu}
            onPin={onPin}
            onEdit={onEdit}
            onReport={onReport}
            onDelete={onDelete}
          />
        </div>
      </article>

      {comment.repliesList?.length > 0 && (
        <div className="mt-4 space-y-4">
          {comment.repliesList.map((reply) => (
            <DiscussionComment
              key={`answer-${reply.id}`}
              comment={reply}
              depth={depth + 1}
              menuOpenId={menuOpenId}
              onToggleMenu={onToggleMenu}
              onCloseMenu={onCloseMenu}
              onLike={onLike}
              onDislike={onDislike}
              onReply={onReply}
              onPin={onPin}
              onEdit={onEdit}
              onReport={onReport}
              onDelete={onDelete}
            />
          ))}
        </div>
      )}
    </div>
  );
}

function DiscussionSkeleton() {
  return (
    <div className="space-y-4">
      {[1, 2].map((item) => (
        <div key={item} className="h-36 animate-pulse rounded-2xl bg-white/70" />
      ))}
    </div>
  );
}

export default function DoubtsDiscussionSection({ chapterId }) {
  const { isLoggedIn, openAuthPopup } = useAuthContext();
  const { user } = useProfileContext();
  const currentUserId = user?.id ?? null;

  const {
    discussions,
    loading,
    error,
    actionError,
    clearActionError,
    postQuestion,
    postAnswer,
    editQuestion,
    removeQuestion,
    togglePinQuestion,
    submitReport,
    reactToQuestion,
    reactToAnswer,
    editAnswer,
    removeAnswer,
  } = useChapterQuestions(chapterId, currentUserId, isLoggedIn && Boolean(chapterId));

  const [askModalOpen, setAskModalOpen] = useState(false);
  const [replyTarget, setReplyTarget] = useState(null);
  const [editTarget, setEditTarget] = useState(null);
  const [reportTarget, setReportTarget] = useState(null);
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [menuOpenId, setMenuOpenId] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [toast, setToast] = useState(null);

  const modalConfig = useMemo(() => {
    if (editTarget) {
      return {
        open: true,
        title: editTarget.type === 'question' ? 'Edit Question' : 'Edit Answer',
        placeholder: 'Update your text...',
        submitLabel: 'Save',
        initialValue: editTarget.text,
        onPost: async (content) => {
          setSubmitting(true);
          const success =
            editTarget.type === 'question'
              ? await editQuestion(editTarget.id, content)
              : await editAnswer(editTarget.id, content);
          setSubmitting(false);
          if (success) {
            setEditTarget(null);
            setToast({ type: 'success', message: 'Updated successfully' });
          }
        },
        onClose: () => !submitting && setEditTarget(null),
      };
    }

    if (replyTarget) {
      return {
        open: true,
        title: 'Post Your Reply',
        placeholder: 'Type your answer...',
        submitLabel: 'Reply',
        initialValue: '',
        onPost: async (content) => {
          setSubmitting(true);
          const success = await postAnswer(replyTarget.id, content);
          setSubmitting(false);
          if (success) {
            setReplyTarget(null);
            setToast({ type: 'success', message: 'Reply posted' });
          }
        },
        onClose: () => !submitting && setReplyTarget(null),
      };
    }

    return {
      open: askModalOpen,
      title: 'Ask Your Questions',
      placeholder: 'Type your Questions or Doubts...',
      submitLabel: 'Post',
      initialValue: '',
      onPost: async (content) => {
        setSubmitting(true);
        const success = await postQuestion(content);
        setSubmitting(false);
        if (success) {
          setAskModalOpen(false);
          setToast({ type: 'success', message: 'Question posted' });
        }
      },
      onClose: () => !submitting && setAskModalOpen(false),
    };
  }, [
    askModalOpen,
    editAnswer,
    editQuestion,
    editTarget,
    postAnswer,
    postQuestion,
    replyTarget,
    submitting,
  ]);

  const requireAuth = (action) => {
    if (!isLoggedIn) {
      openAuthPopup();
      return;
    }
    action();
  };

  const handleLike = (comment) => {
    requireAuth(async () => {
      if (comment.type === 'question') {
        await reactToQuestion(comment.id, 'like');
      } else {
        await reactToAnswer(comment.questionId, comment.id, 'like');
      }
    });
  };

  const handleDislike = (comment) => {
    requireAuth(async () => {
      if (comment.type === 'question') {
        await reactToQuestion(comment.id, 'dislike');
      } else {
        await reactToAnswer(comment.questionId, comment.id, 'dislike');
      }
    });
  };

  const handleReply = (comment) => {
    requireAuth(() => setReplyTarget(comment));
  };

  const handlePin = (comment) => {
    requireAuth(async () => {
      await togglePinQuestion(comment.id, comment.isPinned);
    });
  };

  const handleEdit = (comment) => {
    requireAuth(() => setEditTarget(comment));
  };

  const handleReport = (comment) => {
    requireAuth(() => setReportTarget(comment));
  };

  const handleDelete = (comment) => {
    requireAuth(() => setDeleteTarget(comment));
  };

  const deleteModalCopy = deleteTarget
    ? {
        title:
          deleteTarget.type === 'question' ? 'Delete this question?' : 'Delete this answer?',
        message:
          deleteTarget.type === 'question'
            ? 'This will remove the question and all its answers permanently.'
            : 'This answer will be removed permanently.',
      }
    : { title: '', message: '' };

  return (
    <section className="mt-12">
      <AskQuestionModal {...modalConfig} submitting={submitting} />
      <ReportQuestionModal
        open={Boolean(reportTarget)}
        submitting={submitting}
        onClose={() => !submitting && setReportTarget(null)}
        onSubmit={async (payload) => {
          if (!reportTarget) return;
          setSubmitting(true);
          const success = await submitReport(reportTarget.id, payload);
          setSubmitting(false);
          if (success) {
            setReportTarget(null);
            setToast({ type: 'success', message: 'Report submitted' });
          }
        }}
      />
      <ConfirmDeleteModal
        open={Boolean(deleteTarget)}
        title={deleteModalCopy.title}
        message={deleteModalCopy.message}
        loading={submitting}
        onClose={() => !submitting && setDeleteTarget(null)}
        onConfirm={async () => {
          if (!deleteTarget) return;
          setSubmitting(true);
          const success =
            deleteTarget.type === 'question'
              ? await removeQuestion(deleteTarget.id)
              : await removeAnswer(deleteTarget.id);
          setSubmitting(false);
          if (success) {
            setDeleteTarget(null);
            setMenuOpenId(null);
            setToast({
              type: 'success',
              message:
                deleteTarget.type === 'question' ? 'Question deleted' : 'Answer deleted',
            });
          }
        }}
      />

      {(toast || actionError) && (
        <div
          className={`mb-4 rounded-lg px-4 py-3 text-sm text-white ${
            actionError ? 'bg-red-600' : 'bg-[#2e7d32]'
          }`}
          role="status"
        >
          {actionError || toast?.message}
          <button
            type="button"
            onClick={() => {
              setToast(null);
              clearActionError();
            }}
            className="ml-3 underline"
          >
            Dismiss
          </button>
        </div>
      )}

      <div className="flex flex-wrap items-start justify-between gap-4">
        <div>
          <h2 className="text-xl font-bold text-[#1B1B1B]">Doubts &amp; Discussion</h2>
          <p className="mt-1 text-sm text-[#666666]">Ask question related to this chapter</p>
        </div>
        <button
          type="button"
          onClick={() => requireAuth(() => setAskModalOpen(true))}
          disabled={!chapterId}
          className="inline-flex items-center gap-2 rounded-md bg-[#9369CC] px-5 py-2.5 text-sm font-semibold text-white shadow-md transition hover:opacity-90 disabled:cursor-not-allowed disabled:opacity-50"
        >
          Ask Question
          <span className="flex h-5 w-5 items-center justify-center rounded-full bg-white/20 text-base leading-none">
            +
          </span>
        </button>
      </div>

      <div className="mt-6 bg-[#D6E9FB] px-4 py-10 sm:px-10 sm:py-16">
        {!isLoggedIn ? (
          <p className="text-center text-sm text-[#666666]">
            Please log in to view and participate in chapter discussions.
          </p>
        ) : loading ? (
          <DiscussionSkeleton />
        ) : error ? (
          <p className="text-center text-sm text-red-600">{error}</p>
        ) : discussions.length === 0 ? (
          <p className="text-center text-sm text-[#666666]">
            No questions yet. Be the first to ask!
          </p>
        ) : (
          <div className="space-y-6">
            {discussions.map((discussion) => (
              <DiscussionComment
                key={`question-${discussion.id}`}
                comment={discussion}
                menuOpenId={menuOpenId}
                onToggleMenu={(id) => setMenuOpenId((current) => (current === id ? null : id))}
                onCloseMenu={() => setMenuOpenId(null)}
                onLike={handleLike}
                onDislike={handleDislike}
                onReply={handleReply}
                onPin={handlePin}
                onEdit={handleEdit}
                onReport={handleReport}
                onDelete={handleDelete}
              />
            ))}
          </div>
        )}
      </div>
    </section>
  );
}
